import { useMemo, useCallback, useState, useEffect } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
} from "@headlessui/react";
import { XMarkIcon, ComputerDesktopIcon } from "@heroicons/react/24/outline";
import { LibraryBrowser } from "@/ui-components/LibraryBrowser";
import { portfolioToLibrarySections } from "@/ui-components/LibraryBrowser/adapters";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import { getTagsByIds, tagManager } from "@/shared/api/newsquawk-headlines";
import { useFilterBuilder, type FilterDraft } from "../hooks/useFilterBuilder";
import type { Portfolio } from "@/types/Portfolio";
import { placeholderTagTitle, isPlaceholderTag } from "../hooks/tagCategories";
import QueryBuilderPane from "./QueryBuilderPane";
import DismissibleTip from "@/shared/components/DismissibleTip";
import { TAG_LIBRARY_HINT } from "@/ui-components/TagLibraryResults";

/**
 * Tags found in the library are toggled against the Newsquawk section, whose
 * single-tag rows they join. Module-level so the context memo sees one object.
 */
const TAG_LIBRARY = { sectionId: "nq-library" } as const;

// Static seed from menuCategories
const menuTagIdToName: Record<string, string> = {};
menuCategories.forEach((cat) => {
  cat.items.forEach((item) => {
    menuTagIdToName[String(item.id)] = item.title;
  });
});

interface FilterBuilderBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  queryId?: number | null;
  /** Concierge draft pre-fill (create mode) */
  draft?: FilterDraft | null;
  /** Guided demo only — see UseFilterBuilderOptions.portfoliosOverride. */
  portfoliosOverride?: Portfolio[];
}

export default function FilterBuilderBrowser({
  isOpen,
  onClose,
  queryId,
  draft,
  portfoliosOverride,
}: FilterBuilderBrowserProps) {
  const builder = useFilterBuilder({
    queryId,
    draft,
    portfoliosOverride,
    onSaved: () => onClose(),
    onDeleted: () => onClose(),
  });

  const handleClose = useCallback(() => {
    builder.reset();
    onClose();
  }, [builder.reset, onClose]);

  // Dynamic tag name resolution
  const [resolvedTagNames, setResolvedTagNames] = useState<Record<string, string>>({});

  const tagIdToName = useMemo(
    () => ({ ...menuTagIdToName, ...resolvedTagNames }),
    [resolvedTagNames]
  );

  useEffect(() => {
    /**
     * The selected tags belong here as much as the watchlists' do. This used to
     * resolve only `tagsWatchlists`, so a tag the concierge picked from outside
     * the 51 menu ones had no name to show and no row to sit in.
     */
    const allTagIds = [
      ...builder.tagsWatchlists.flatMap((tw) => tw.tags),
      ...builder.selectedCategories.map((c) => String(c.id)),
    ];
    const unknownIds = allTagIds.filter((id) => !menuTagIdToName[id] && !resolvedTagNames[id]);
    if (unknownIds.length === 0) return;

    const uniqueIds = [...new Set(unknownIds)];
    getTagsByIds(uniqueIds.join(" "))
      .then((tagDetails) => {
        const newNames: Record<string, string> = {};
        if (tagDetails) {
          for (const [tagId, detail] of Object.entries(tagDetails)) {
            const primary = detail.labels?.find((l) => l.primary);
            const name = primary?.name || detail.labels?.[0]?.name;
            if (name) newNames[tagId] = name;
          }
        }
        if (Object.keys(newNames).length > 0) {
          setResolvedTagNames((prev) => ({ ...prev, ...newNames }));
        }
      })
      .catch(() => {});
  }, [builder.tagsWatchlists, builder.selectedCategories]);

  // Swap the `Tag #123` placeholders the builder seeded for the real names, so
  // the chips and the summary read as tags rather than as ids.
  useEffect(() => {
    const needsRename = builder.selectedCategories.some(
      (c) => isPlaceholderTag(c) && resolvedTagNames[String(c.id)]
    );
    if (!needsRename) return;
    builder.setSelectedCategories((prev) =>
      prev.map((c) => {
        const name = resolvedTagNames[String(c.id)];
        return name && isPlaceholderTag(c) ? { ...c, title: name } : c;
      })
    );
  }, [resolvedTagNames, builder.selectedCategories, builder.setSelectedCategories]);

  // Tags held selected that the curated menu doesn't list. Without a row of
  // their own the Active pane counts them and then renders an empty list.
  const extraTags = useMemo(
    () =>
      builder.selectedCategories
        .filter((c) => c.id != null && !menuTagIdToName[String(c.id)])
        .map((c) => ({
          id: String(c.id),
          name: tagIdToName[String(c.id)] ?? c.title,
        })),
    [builder.selectedCategories, tagIdToName]
  );

  // Build LibrarySection[] from portfolios + tags
  const sections = useMemo(() => {
    return portfolioToLibrarySections(
      builder.portfolios,
      builder.tagsWatchlists,
      [],
      { tagIdToName, extraTags }
    );
  }, [builder.portfolios, builder.tagsWatchlists, tagIdToName, extraTags]);

  // Map builder selections to LibraryBrowser selections format
  // The builder tracks portfolioIds, categoryIds, and tagWatchlistIds separately.
  // We need to map them to section-based selections for the LibraryBrowser.
  const browserSelections = useMemo<Record<string, string[]>>(() => {
    const sel: Record<string, string[]> = {};

    /**
     * Keys here must be section ids, because that is what the browser matches
     * them against — Active resolves a selection by finding the section whose
     * id equals the key and then the item inside it.
     *
     * They used to be split by kind (my-ticker-watchlists, my-keyword-
     * watchlists, my-tag-watchlists) from when the adapter emitted a section
     * per type. It now emits one "my-watchlists", so none of those keys matched
     * anything and every selection the user owned resolved to nothing: Active
     * counted them in its badge and then showed an empty pane. Newsquawk's kept
     * working, which is what made it look like a loading fault rather than a
     * lookup one.
     */
    const mine: string[] = [];
    const desk: string[] = [];

    for (const id of builder.selectedPortfolioIds) {
      const p = builder.portfolios.find((port) => port.id === id);
      if (!p) continue;
      (p.owner_type === "NEWSQUAWK" ? desk : mine).push(String(id));
    }

    for (const id of builder.selectedTagsWatchlistIds) {
      const tw = builder.tagsWatchlists.find((t) => t.id === id);
      (tw?.owner_type === "USER" ? mine : desk).push(`tag-${id}`);
    }

    if (mine.length > 0) sel["my-watchlists"] = mine;
    if (desk.length > 0) sel["nq-library"] = desk;

    // Individual tag categories → go to newsquawk library section
    if (builder.selectedCategories.length > 0) {
      const existing = sel["nq-library"] || [];
      sel["nq-library"] = [
        ...existing,
        ...builder.selectedCategories.map((c) => String(c.id)),
      ];
    }

    return sel;
    // tagsWatchlists is read to decide whose a tag list is, so it belongs here:
    // without it, lists arriving after first render keep their stale owner.
  }, [builder.selectedPortfolioIds, builder.selectedTagsWatchlistIds, builder.selectedCategories, builder.portfolios, builder.tagsWatchlists]);

  // Handle toggle from LibraryBrowser
  const handleToggle = useCallback(
    (_sectionId: string, itemId: string) => {
      // Tag watchlist items have "tag-" prefix
      if (itemId.startsWith("tag-")) {
        const twId = Number(itemId.replace("tag-", ""));
        if (!isNaN(twId)) {
          builder.toggleTagWatchlist(twId);
        }
        return;
      }

      // Try as a portfolio ID first (tickers/keywords)
      const numId = Number(itemId);
      if (!isNaN(numId)) {
        const portfolio = builder.portfolios.find((p) => p.id === numId);
        if (portfolio) {
          builder.togglePortfolio(numId);
          return;
        }

        /**
         * Otherwise it's an individual tag. The title is looked up rather than
         * required: gating the toggle on a `menuCategories` hit meant clicking
         * any tag outside those 51 did nothing at all — no chip, no warning —
         * and the concierge emits ids from the whole tag system.
         */
        builder.toggleCategory({
          title:
            tagIdToName[String(numId)] ??
            // A tag just picked out of the library search is already named in
            // the shared cache, so this costs no second lookup.
            tagManager.getCached(numId)?.name ??
            placeholderTagTitle(numId),
          id: numId,
        });
      }
    },
    [builder.togglePortfolio, builder.toggleTagWatchlist, builder.toggleCategory, builder.portfolios, tagIdToName]
  );

  // Filter out advanced filters since we're building one — strip from both
  // standalone sections and the Newsquawk Library's groups
  const filteredSections = useMemo(
    () => sections
      .filter((s) => s.id !== "my-filters")
      .map((s) => {
        if (s.id === "nq-library") {
          const groups = s.groups.filter((g) => g.id !== "newsquawk-filters");
          return groups.length > 0 ? { ...s, groups } : null;
        }
        return s;
      })
      .filter((s): s is NonNullable<typeof s> => s !== null),
    [sections]
  );

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  return (
    <Dialog open={isOpen} onClose={handleClose} className="no-drag relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/90 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200"
      />
      <div className="fixed inset-0 z-10 flex items-center justify-center p-2 md:p-4">
        <DialogPanel
          transition
          className="w-full h-[90vh] mx-2 md:mx-4 transform overflow-hidden rounded-lg bg-gray-800 shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[closed]:sm:scale-95"
        >
          {/* Mobile fallback */}
          {isMobile ? (
            <div className="h-full flex flex-col items-center justify-center px-8 text-center">
              <ComputerDesktopIcon className="size-12 text-gray-600 mb-4" />
              <h3 className="text-sm font-semibold text-gray-200 mb-2">Desktop Required</h3>
              <p className="text-xs text-gray-500 mb-6">
                Advanced filter editing is available on larger screens.
              </p>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium rounded bg-gray-700 text-gray-200 hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
          <LibraryBrowser
            mode="builder"
            sections={filteredSections}
            selections={browserSelections}
            onSelectionsChange={() => {}}
            onToggleOverride={handleToggle}
            className="h-full !rounded-none !border-0"
            sidebarTitle="Watchlists & tags"
            /* No defaultActivePath: opening straight into the whole Newsquawk
         taxonomy made a wall of checkboxes the first thing in the editor,
         outweighing the form that is actually the job. They appear when
         "Newsquawk watchlists" is chosen. */
            /* The two rules this screen never states. A row's shape is the only
               clue to what it is, and "Active" reads as a status rather than
               the dashboard's current selection. */
            sidebarTip={
              <DismissibleTip id="library-browser-groups">
                Active is what your dashboard is filtering on now. Newsquawk
                lists are desk-built and ready to use as they are.
              </DismissibleTip>
            }
            tagLibrary={TAG_LIBRARY}
            listTip={
              <>
                <DismissibleTip id="library-browser-rows">
                  Rows that expand hold several tickers or keywords. The rest are
                  single topic tags.
                </DismissibleTip>
                <p className="pt-1.5 text-2xs text-gray-500">
                  {TAG_LIBRARY_HINT}
                </p>
              </>
            }
            customRightPane={<QueryBuilderPane builder={builder} onCancel={handleClose} />}
            header={
              <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-700/50 bg-gray-800">
                <h2 className="text-sm font-semibold text-gray-100 flex-1">
                  {builder.isEditing ? "Edit watchlist" : "New watchlist — match several at once"}
                </h2>
                <button
                  onClick={handleClose}
                  className="p-1 text-gray-400 hover:text-gray-200 transition-colors"
                >
                  <XMarkIcon className="size-5" />
                </button>
              </div>
            }
          />
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
}
